import { useTracking } from '@act/react-components/tracking';
import { CallDto, UserDto } from '@act/shared/data-transfer-objects';
import {
  Button,
  IconAvatar,
  IconLibrary,
  MemberStatusTag,
} from '@act/storybook';
import { useMobile } from '@act/utilities/mobile-context';
import { Box, CircularProgress, Typography } from '@mui/material';
import { getCallProgressionStatus } from './CallsFullScreenContent.utils';

interface CallHeaderProps {
  currentCall: CallDto | undefined;
  newCall: CallDto | undefined;
  selectedMember: UserDto | undefined;
  memberNumber: string;
  isResource: boolean;
  resourceTitle: string | undefined;
  resourceNumber: string | undefined;
  topContentLeftOnly: boolean;
  onBeginCall: () => void;
  disabledBeginCall: boolean;
  isCreateCallLoading: boolean;
}

export const CallHeader = ({
  currentCall,
  newCall,
  selectedMember,
  memberNumber,
  isResource,
  resourceTitle,
  resourceNumber,
  topContentLeftOnly,
  onBeginCall,
  disabledBeginCall,
  isCreateCallLoading,
}: CallHeaderProps) => {
  const { isMobile } = useMobile();
  const { generateTrackingProps } = useTracking();
  const progressionStatus = getCallProgressionStatus(currentCall, newCall);

  const renderCallStatus = (
    iconName: string,
    text: string,
    iconColor?: string
  ) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile || topContentLeftOnly ? '6px' : '12px',
      }}
    >
      <IconLibrary iconName={iconName} size={20} color={iconColor} />
      <Typography variant="bodyMedium" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );

  const getCallProgressionContent = () => {
    if (!progressionStatus) return null;

    switch (progressionStatus.type) {
      case 'failed':
        return renderCallStatus(
          'PhoneOutgoing',
          progressionStatus.text,
          'error'
        );
      case 'calling':
        return renderCallStatus(
          'PhoneOutgoing',
          progressionStatus.text,
          'tertiary'
        );
      case 'completed':
        return renderCallStatus('PhoneEnded', progressionStatus.text);
      case 'connected':
        return renderCallStatus(
          'PhoneOutgoing',
          progressionStatus.text,
          'success'
        );
      default:
        return null;
    }
  };

  const callProgressionContent = getCallProgressionContent();
  const isCalling =
    progressionStatus?.type === 'calling' || isCreateCallLoading;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '16px',
        left: '16px',
        width:
          (topContentLeftOnly || !currentCall) && !isMobile
            ? 'calc(50% - 76px)'
            : 'calc(100% - 72px)',
        zIndex: 10,
        padding: '12px 20px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'space-evenly' : 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: isMobile ? '8px' : '12px',
            alignItems: 'center',
          }}
        >
          {isResource ? (
            <IconAvatar avatarType="list_magnifying_glass" size={24} />
          ) : (
            <IconAvatar avatarType="phone" size={24} />
          )}
          <Typography
            variant="titleLarge"
            color="text.primary"
            sx={{ alignSelf: 'center' }}
          >
            {isResource
              ? resourceTitle
              : `${selectedMember?.firstName} ${selectedMember?.lastName}`}
          </Typography>
          {!isResource && (
            <MemberStatusTag status={selectedMember?.memberStatus} />
          )}
        </Box>
        <Typography
          variant="bodyMedium"
          color="text.secondary"
          sx={{ marginLeft: '62px' }}
        >
          {isResource ? resourceNumber : memberNumber}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          justifyContent: 'space-between',
          marginLeft: isMobile ? '10px' : undefined,
        }}
      >
        {!currentCall && (
          <Button
            trackingName="call-now-button"
            variant="contained"
            {...generateTrackingProps('call-now-button')}
            startIcon={
              isCalling ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <IconLibrary
                  iconName="PhoneOutgoing"
                  size={20}
                  color="primary"
                />
              )
            }
            onClick={() => {
              onBeginCall();
            }}
            disabled={disabledBeginCall || isCalling}
          >
            {isCalling ? progressionStatus?.text || 'Calling...' : 'Call Now'}
          </Button>
        )}
        {callProgressionContent}
      </Box>
    </Box>
  );
};
